/**
 * TPL-006: JSON Schema loader for slide validation.
 *
 * This module loads JSON Schema contracts from the slide-schemas package
 * and provides utilities for schema-driven validation.
 *
 * Usage:
 *   import { getSchemaLimits, validateAgainstSchemaLimits } from './schema-loader';
 *   const limits = getSchemaLimits('quadrant-map');
 *   const errors = validateAgainstSchemaLimits('quadrant-map', slideData);
 */

import Ajv2020 from 'ajv/dist/2020';
import type { ErrorObject } from 'ajv';

import { PLOT_SCHEMAS, SCHEMAS, SHARED_SCHEMAS, SlideSchema } from './generated/slide-schema-registry.generated';

export interface SchemaProperty {
  type?: string | string[];
  maxLength?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
  items?: SchemaProperty;
  properties?: Record<string, SchemaProperty>;
  $ref?: string;
  allOf?: SchemaProperty[];
  anyOf?: SchemaProperty[];
  oneOf?: SchemaProperty[];
}

export interface SchemaLimits {
  maxItems: Record<string, number>;
  maxLength: Record<string, number>;
}

let ajv: Ajv2020 | null = null;

function schemaAjv(): Ajv2020 {
  if (ajv) {
    return ajv;
  }
  ajv = new Ajv2020({
    allErrors: true,
    strict: false,
    validateSchema: false,
  });
  for (const [key, schema] of Object.entries(SHARED_SCHEMAS)) {
    try {
      ajv.addSchema(schema, key);
    } catch {
      // Multiple aliases can point to the same shared schema; ignore duplicates.
    }
  }
  for (const [key, schema] of Object.entries(SCHEMAS)) {
    try {
      ajv.addSchema(schema, key);
    } catch {
      // The generated registry can be imported more than once in test runners.
    }
  }
  // The plot registry: each kind by its `$id` (`plot-<kind>`, since the file
  // names repeat slide types); `common` by the file name the kinds `$ref`.
  for (const [stem, schema] of Object.entries(PLOT_SCHEMAS)) {
    try {
      if (stem === 'common') {
        const { $id: _id, ...common } = schema;
        ajv.addSchema(common, 'common.schema.json');
      } else {
        ajv.addSchema(schema, `plot-${stem}`);
      }
    } catch {
      // The generated registry can be imported more than once in test runners.
    }
  }
  return ajv;
}

function decodeJsonPointerSegment(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~');
}

function resolveJsonPointer(target: unknown, pointer: string): SchemaProperty | null {
  if (!pointer || pointer === '#') {
    return (target as SchemaProperty) ?? null;
  }
  const cleaned = pointer.startsWith('#') ? pointer.slice(1) : pointer;
  const path = cleaned.startsWith('/') ? cleaned.slice(1).split('/') : cleaned.split('/');
  let current: unknown = target;
  for (const rawSegment of path) {
    const segment = decodeJsonPointerSegment(rawSegment);
    if (!segment) {
      continue;
    }
    if (!current || typeof current !== 'object' || !(segment in (current as Record<string, unknown>))) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return (current as SchemaProperty) ?? null;
}

function resolveRef(ref: string, currentSchema: SlideSchema): SchemaProperty | null {
  const [schemaPartRaw, pointerRaw] = ref.split('#', 2);
  const schemaPart = schemaPartRaw.trim();
  const pointer = pointerRaw ? `#${pointerRaw}` : '#';

  if (!schemaPart) {
    return resolveJsonPointer(currentSchema, pointer);
  }

  const normalizedSchemaPart = schemaPart.replace(/^\.\//, '');
  const schemaFromShared = SHARED_SCHEMAS[schemaPart] || SHARED_SCHEMAS[normalizedSchemaPart];
  if (schemaFromShared) {
    return resolveJsonPointer(schemaFromShared, pointer);
  }

  const key = normalizedSchemaPart.endsWith('.schema.json')
    ? normalizedSchemaPart.replace(/\.schema\.json$/, '')
    : normalizedSchemaPart;
  const schemaFromSlides = SCHEMAS[key];
  if (!schemaFromSlides) {
    return null;
  }
  return resolveJsonPointer(schemaFromSlides, pointer);
}

function resolveProperty(property: SchemaProperty, currentSchema: SlideSchema): SchemaProperty {
  if (!property.$ref) {
    return property;
  }
  const resolved = resolveRef(property.$ref, currentSchema);
  if (!resolved) {
    return property;
  }
  const { $ref: _, ...rest } = property;
  return {
    ...resolved,
    ...rest,
  };
}

function collectCompositeNodes(property: SchemaProperty): SchemaProperty[] {
  const composites: SchemaProperty[] = [];
  if (Array.isArray(property.allOf)) composites.push(...property.allOf);
  if (Array.isArray(property.anyOf)) composites.push(...property.anyOf);
  if (Array.isArray(property.oneOf)) composites.push(...property.oneOf);
  return composites;
}

/**
 * The top-level composite branches of a slide schema, `$ref` resolved.
 *
 * A slide that inherits from base declares `allOf: [{ $ref: base }]`; the
 * branch itself has no `properties`, so reading it unresolved saw none of the
 * fields the slide takes from base.
 */
function resolvedTopLevelComposites(schema: SlideSchema): Array<SchemaProperty> {
  return collectCompositeNodes(schema as SchemaProperty).map(composite => resolveProperty(composite, schema))
}

/**
 * Get the JSON Schema for a slide type.
 */
export function getSchema(slideType: string): SlideSchema | null {
  return SCHEMAS[slideType] || null;
}

function errorPath(error: ErrorObject): string {
  const base = error.instancePath
    ? error.instancePath.slice(1).replace(/\//g, '.')
    : '';
  if (error.keyword === 'required') {
    const missing = (error.params as { missingProperty?: string }).missingProperty;
    return [base, missing].filter(Boolean).join('.') || missing || 'slide';
  }
  return base || 'slide';
}

function expectedForError(error: ErrorObject): string {
  switch (error.keyword) {
    case 'required':
      return 'required field';
    case 'type':
      return String((error.params as { type?: unknown }).type ?? 'valid type');
    case 'const':
      return `const ${JSON.stringify((error.params as { allowedValue?: unknown }).allowedValue)}`;
    case 'enum':
      return 'one of allowed enum values';
    case 'minItems':
      return `at least ${(error.params as { limit?: number }).limit} items`;
    case 'maxItems':
      return `at most ${(error.params as { limit?: number }).limit} items`;
    case 'minLength':
      return `length >= ${(error.params as { limit?: number }).limit}`;
    case 'maxLength':
      return `length <= ${(error.params as { limit?: number }).limit}`;
    case 'minimum':
      return `>= ${(error.params as { comparison?: string; limit?: number }).limit}`;
    case 'maximum':
      return `<= ${(error.params as { comparison?: string; limit?: number }).limit}`;
    default:
      return error.keyword;
  }
}

export interface JsonSchemaValidationIssue {
  path: string;
  expected: string;
  received: string;
  message: string;
}

function toIssue(error: ErrorObject, data: Record<string, unknown>, prefix: string): JsonSchemaValidationIssue {
  const inner = errorPath(error);
  const path = prefix ? (inner === 'slide' ? prefix : `${prefix}.${inner}`) : inner;
  return {
    path,
    expected: expectedForError(error),
    received: describeSchemaValue(data, error.instancePath),
    message: error.message ? `${path} ${error.message}` : `Invalid value at ${path}`,
  };
}

/**
 * A slide's `plot` against its kind's schema (packages/slide-schemas/plots).
 * The slide schema checks only the envelope (`kind` is a plot kind).
 */
export function validatePlotAgainstJsonSchema(plot: Record<string, unknown>): JsonSchemaValidationIssue[] {
  const kind = typeof plot.kind === 'string' ? plot.kind : null;
  const schema = kind && kind !== 'common' ? PLOT_SCHEMAS[kind] : undefined;
  if (!schema) {
    return [{
      path: 'plot.kind',
      expected: Object.keys(PLOT_SCHEMAS).filter((stem) => stem !== 'common').join(' | '),
      received: kind ?? typeof plot.kind,
      message: `plot.kind '${String(plot.kind)}' is not a registered plot kind`,
    }];
  }
  const validator = schemaAjv().getSchema(`plot-${kind}`) ?? schemaAjv().compile(schema);
  if (validator(plot)) {
    return [];
  }
  return (validator.errors ?? []).map((error) => toIssue(error, plot, 'plot'));
}

export function validateAgainstJsonSchema(
  slideType: string,
  data: Record<string, unknown>,
): JsonSchemaValidationIssue[] {
  const schema = getSchema(slideType);
  if (!schema) {
    return [];
  }
  const issues: JsonSchemaValidationIssue[] = [];
  if ('id' in data && (typeof data.id !== 'string' || data.id.length === 0)) {
    issues.push({
      path: 'id',
      expected: 'non-empty string',
      received: describeSchemaValue(data, '/id'),
      message: 'id must be a non-empty string',
    });
  }
  // Validate the renderer payload without the canonical id or the stored
  // plot. The original slide keeps its id for rendering and traceability;
  // plot validity is handled separately by validateStoredPlot.
  const slide = rendererPayload(data);
  const validator = schemaAjv().getSchema(slideType) ?? schemaAjv().compile(schema);
  if (!validator(slide)) {
    issues.push(...(validator.errors ?? []).map((error) => toIssue(error, slide, '')));
  }
  return issues;
}

function rendererPayload(data: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...data };
  delete rest.id;
  delete rest.plot;
  return rest;
}

/**
 * Read-path check of a stored slide's `plot` (envelope + kind schema).
 * Issues are warnings for the viewer: the slide renders without the plot
 * (its legacy fields). Strict plot validation happens at build time in
 * Python (slide_contracts.validate_plot_payload).
 */
export function validateStoredPlot(data: Record<string, unknown>): JsonSchemaValidationIssue[] {
  if (!('plot' in data) || data.plot === undefined) {
    return [];
  }
  const plot = data.plot;
  if (!plot || typeof plot !== 'object' || Array.isArray(plot)) {
    return [{ path: 'plot', expected: 'object', received: Array.isArray(plot) ? 'array' : typeof plot, message: 'plot must be an object' }];
  }
  // The plot is checked without its alternatives, then each alternative on
  // its own, so one bad alternative never hides another or the plot.
  const record = plot as Record<string, unknown>;
  const { alternatives } = record;
  const main = { ...record };
  delete main.alternatives;
  const issues = validatePlotAgainstJsonSchema(main);
  if (issues.length > 0 || alternatives === undefined) {
    return issues;
  }
  if (!Array.isArray(alternatives)) {
    return [{ path: 'plot.alternatives', expected: 'array', received: typeof alternatives, message: 'plot.alternatives must be an array' }];
  }
  return alternatives.flatMap((alternative, index) => {
    if (!alternative || typeof alternative !== 'object' || Array.isArray(alternative)) {
      return [{ path: `plot.alternatives.${index}`, expected: 'object', received: typeof alternative, message: `plot.alternatives.${index} must be an object` }];
    }
    return validateStoredAlternative(String(record.kind), alternative as Record<string, unknown>, index);
  });
}

/**
 * One stored alternative: a registry alternative of the plot's kind whose
 * data satisfies that kind. Its issues sit under `plot.alternatives.<i>`; the
 * viewer drops only that alternative (see STORED_ALTERNATIVE_PATH).
 */
function validateStoredAlternative(
  kind: string,
  alternative: Record<string, unknown>,
  index: number,
): JsonSchemaValidationIssue[] {
  const path = `plot.alternatives.${index}`;
  const registry = (PLOT_SCHEMAS[kind] as { 'x-plot'?: { alternatives?: Array<string> } } | undefined)?.['x-plot'];
  const allowed = registry?.alternatives ?? [];
  if (!allowed.includes(String(alternative.kind))) {
    return [{
      path: `${path}.kind`,
      expected: allowed.join(' | ') || 'no alternatives',
      received: String(alternative.kind),
      message: `${path}.kind '${String(alternative.kind)}' is not a registry alternative of '${kind}'`,
    }];
  }
  return validatePlotAgainstJsonSchema({ kind: alternative.kind, data: alternative.data }).map((issue) => ({
    ...issue,
    path: issue.path.replace(/^plot/, path),
    message: issue.message.replace(/^plot/, path),
  }));
}

/** Matches the path of an issue that belongs to one stored alternative. */
export const STORED_ALTERNATIVE_PATH = /^plot\.alternatives\.(\d+)(\.|$)/;

function describeSchemaValue(data: Record<string, unknown>, instancePath: string): string {
  if (!instancePath) {
    return typeof data;
  }
  const path = instancePath.slice(1).split('/').map(decodeJsonPointerSegment);
  let current: unknown = data;
  for (const segment of path) {
    if (Array.isArray(current)) {
      current = current[Number(segment)];
    } else if (current && typeof current === 'object') {
      current = (current as Record<string, unknown>)[segment];
    } else {
      current = undefined;
      break;
    }
  }
  if (current === undefined) return 'missing';
  if (current === null) return 'null';
  if (Array.isArray(current)) return 'array';
  return typeof current;
}

/**
 * Extract maxItems and maxLength limits from a schema.
 */
export function getSchemaLimits(slideType: string): SchemaLimits {
  const schema = getSchema(slideType);
  const limits: SchemaLimits = {
    maxItems: {},
    maxLength: {},
  };

  if (!schema) {
    return limits;
  }

  const collectLimits = (property: SchemaProperty, path: string): void => {
    const resolved = resolveProperty(property, schema);

    if (resolved.maxItems !== undefined) {
      limits.maxItems[path] = resolved.maxItems;
    }
    if (resolved.maxLength !== undefined) {
      limits.maxLength[path] = resolved.maxLength;
    }

    if (resolved.items) {
      collectLimits(resolved.items, `${path}.items`);
    }

    if (resolved.properties) {
      for (const [nestedKey, nestedProp] of Object.entries(resolved.properties)) {
        collectLimits(nestedProp, `${path}.${nestedKey}`);
      }
    }

    for (const composite of collectCompositeNodes(resolved)) {
      collectLimits(composite, path);
    }
  };

  if (schema.properties) {
    for (const [key, prop] of Object.entries(schema.properties)) {
      collectLimits(prop, key);
    }
  }

  for (const composite of resolvedTopLevelComposites(schema)) {
    if (!composite.properties) continue;
    for (const [key, prop] of Object.entries(composite.properties)) {
      // A local redeclaration tightens base (eyebrow 40 vs 80): keep it.
      if (schema.properties && key in schema.properties) {
        continue;
      }
      collectLimits(prop, key);
    }
  }

  return limits;
}

/**
 * Validate data against schema limits (maxItems, maxLength).
 * Returns array of validation error messages.
 */
export function validateAgainstSchemaLimits(
  slideType: string,
  data: Record<string, unknown>
): string[] {
  const schema = getSchema(slideType);
  if (!schema) {
    return [];
  }

  const errors: string[] = [];
  const validateNode = (
    property: SchemaProperty,
    path: string,
    value: unknown,
  ): void => {
    const resolved = resolveProperty(property, schema);

    if (resolved.maxItems !== undefined && Array.isArray(value) && value.length > resolved.maxItems) {
      errors.push(
        `${path}: array has ${value.length} items, exceeds maxItems=${resolved.maxItems}`,
      );
    }

    if (resolved.maxLength !== undefined && typeof value === 'string' && value.length > resolved.maxLength) {
      errors.push(
        `${path}: string has ${value.length} chars, exceeds maxLength=${resolved.maxLength}`,
      );
    }

    if (resolved.minimum !== undefined && typeof value === 'number' && value < resolved.minimum) {
      errors.push(`${path}: value ${value} is below minimum=${resolved.minimum}`);
    }

    if (resolved.maximum !== undefined && typeof value === 'number' && value > resolved.maximum) {
      errors.push(`${path}: value ${value} exceeds maximum=${resolved.maximum}`);
    }

    if (resolved.items && Array.isArray(value)) {
      value.forEach((item, index) => {
        validateNode(resolved.items as SchemaProperty, `${path}[${index}]`, item);
      });
    }

    if (resolved.properties && value && typeof value === 'object' && !Array.isArray(value)) {
      const valueRecord = value as Record<string, unknown>;
      for (const [nestedKey, nestedProp] of Object.entries(resolved.properties)) {
        if (!(nestedKey in valueRecord)) continue;
        validateNode(nestedProp, `${path}.${nestedKey}`, valueRecord[nestedKey]);
      }
    }

    for (const composite of collectCompositeNodes(resolved)) {
      validateNode(composite, path, value);
    }
  };

  if (schema.properties) {
    for (const [key, prop] of Object.entries(schema.properties)) {
      validateNode(prop, key, data[key]);
    }
  }

  for (const composite of resolvedTopLevelComposites(schema)) {
    if (!composite.properties) continue;
    for (const [key, prop] of Object.entries(composite.properties)) {
      if (!(key in data)) continue;
      validateNode(prop, key, data[key]);
    }
  }

  return errors;
}

/**
 * Recursively sanitize data against schema limits.
 *
 * Trims arrays to maxItems and clamps numbers to minimum/maximum. Never cuts
 * text: contract validation (`validateSlide`) runs first and rejects a string
 * past its maxLength, so one reaching here is a contract bug. It is reported
 * as a warning and left unchanged. Works on
 * deeply nested structures by walking the JSON Schema, resolving $ref
 * pointers along the way.
 *
 * Returns the mutated data and a list of warning messages.
 */
export function sanitizeAgainstSchemaLimits(
  slideType: string,
  data: Record<string, unknown>,
): { warnings: string[] } {
  const schema = getSchema(slideType);
  if (!schema) {
    return { warnings: [] };
  }

  const warnings: string[] = [];

  const sanitizeNode = (
    property: SchemaProperty,
    path: string,
    value: unknown,
    parent: Record<string, unknown> | unknown[],
    key: string | number,
  ): void => {
    const resolved = resolveProperty(property, schema);

    if (resolved.maxLength !== undefined && typeof value === 'string' && value.length > resolved.maxLength) {
      warnings.push(
        `${path}: ${value.length} chars exceeds its ${resolved.maxLength}-char structural ceiling after contract validation; left unchanged`,
      );
    }

    if (resolved.maxItems !== undefined && Array.isArray(value) && value.length > resolved.maxItems) {
      warnings.push(`${path}: trimmed from ${value.length} to ${resolved.maxItems} items`);
      const trimmed = value.slice(0, resolved.maxItems);
      (parent as any)[key] = trimmed;
      value = trimmed;
    }

    if (resolved.minimum !== undefined && typeof value === 'number' && value < resolved.minimum) {
      (parent as any)[key] = resolved.minimum;
    }
    if (resolved.maximum !== undefined && typeof value === 'number' && value > resolved.maximum) {
      (parent as any)[key] = resolved.maximum;
    }

    if (resolved.items && Array.isArray(value)) {
      value.forEach((item, index) => {
        sanitizeNode(resolved.items as SchemaProperty, `${path}[${index}]`, item, value, index);
      });
    }

    if (resolved.properties && value && typeof value === 'object' && !Array.isArray(value)) {
      const rec = value as Record<string, unknown>;
      for (const [nestedKey, nestedProp] of Object.entries(resolved.properties)) {
        if (!(nestedKey in rec)) continue;
        sanitizeNode(nestedProp, `${path}.${nestedKey}`, rec[nestedKey], rec, nestedKey);
      }
    }

    for (const composite of collectCompositeNodes(resolved)) {
      sanitizeNode(composite, path, (parent as any)[key], parent, key);
    }
  };

  const wrapper: Record<string, unknown> = { __root__: data };

  if (schema.properties) {
    for (const [key, prop] of Object.entries(schema.properties)) {
      if (!(key in data)) continue;
      sanitizeNode(prop, key, data[key], data, key);
    }
  }

  for (const composite of resolvedTopLevelComposites(schema)) {
    if (!composite.properties) continue;
    for (const [key, prop] of Object.entries(composite.properties)) {
      if (!(key in data)) continue;
      sanitizeNode(prop, key, data[key], data, key);
    }
  }

  void wrapper;

  return { warnings };
}

/**
 * Check if a slide type has a schema.
 */
export function hasSchema(slideType: string): boolean {
  return slideType in SCHEMAS;
}

export function listSchemaTypes(): Array<string> {
  return Object.keys(SCHEMAS).sort();
}

// ---------------------------------------------------------------------------
// Trust-boundary parsing
// ---------------------------------------------------------------------------

/** Why a payload crossing a trust boundary was not accepted. */
export interface BoundaryParseFailure {
  /** The boundary the payload crossed (an endpoint name), for the log line. */
  boundary: string;
  code: 'schema_mismatch';
  issues: Array<JsonSchemaValidationIssue>;
}

/** A parsed payload, or why it was rejected. Never throws. */
export type BoundaryParseResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: BoundaryParseFailure };

/** Parses an untrusted payload at one boundary. */
export type BoundaryParser<T> = (value: unknown) => BoundaryParseResult<T>;

let boundaryAjvInstance: Ajv2020 | null = null;

function boundaryAjv(): Ajv2020 {
  if (boundaryAjvInstance) {
    return boundaryAjvInstance;
  }
  boundaryAjvInstance = new Ajv2020({
    allErrors: true,
    strict: false,
    validateSchema: false,
  });
  return boundaryAjvInstance;
}

const loggedBoundaryFailures = new Set<string>();

function logBoundaryFailureOnce(failure: BoundaryParseFailure): void {
  if (loggedBoundaryFailures.has(failure.boundary)) {
    return;
  }
  loggedBoundaryFailures.add(failure.boundary);
  console.warn(
    `[boundary] ${failure.boundary}: unexpected payload shape`,
    failure.issues.map((issue) => issue.message),
  );
}

function describeBoundaryValue(value: unknown, instancePath: string): string {
  if (value && typeof value === 'object') {
    return describeSchemaValue(value as Record<string, unknown>, instancePath);
  }
  if (value === null) {
    return 'null';
  }
  return typeof value;
}

/**
 * A parser for one trust boundary: validates with the shared AJV setup and
 * returns `{ ok, data }` or `{ ok: false, reason }`. A mismatch logs once per
 * boundary and never throws, so callers render a typed unavailable state.
 *
 * `schema` is a JSON Schema (draft 2020-12), ideally a generated contract.
 */
export function createBoundaryParser<T>(
  boundary: string,
  schema: Record<string, unknown>,
): BoundaryParser<T> {
  let validator: ReturnType<Ajv2020['compile']> | null = null;
  return (value: unknown) => {
    if (!validator) {
      validator = boundaryAjv().compile(schema);
    }
    if (validator(value)) {
      return { ok: true, data: value as T };
    }
    const failure: BoundaryParseFailure = {
      boundary,
      code: 'schema_mismatch',
      issues: (validator.errors ?? []).map((error) => ({
        path: errorPath(error),
        expected: expectedForError(error),
        received: describeBoundaryValue(value, error.instancePath),
        message: error.message
          ? `${errorPath(error)} ${error.message}`
          : `Invalid value at ${errorPath(error)}`,
      })),
    };
    logBoundaryFailureOnce(failure);
    return { ok: false, reason: failure };
  };
}

/** Test seam: forget which boundaries already logged. */
export function resetBoundaryFailureLog(): void {
  loggedBoundaryFailures.clear();
}
